import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext
} from "react";

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  //updateDoc,
  where
} from "firebase/firestore";

import { createPortal } from "react-dom";
import GlobalNavDrawer from "./GlobalNavDrawer";
import { TokenContext } from "./tokenContext";
import { logout } from "../utils/firebase";
import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import {
  isAdmin,
  isCareerCoach,
  isEmployer,
  isCandidate,
  redirectToJobBankLogin,
  useJobBankRole
} from "./jobBankRole";

import "./CareerCoachPortal.css";
import "./PublicProfiles.css";
import EmployerProfileReadOnly
from "./EmployerProfileReadOnly";
import {
  getHiringEvents,
  getEventRegistrations,
  getMyEventRegistrations,
  registerForHiringEvent,
  updateEventRegistration
} from "../services/eventService";
import {
  bootstrapCoachDirectoryProfiles,
  canBootstrapCoachDirectory,
  ensureCoachDirectoryProfile,
  getCoachDisplayName,
  getPublicContactFields,
  listDirectoryCoaches,
  loadCoachPublicProfileForEdit,
  saveCoachPublicProfile
} from "../services/coachDirectoryService";
import {
  declineRelationship,
  //endRelationship,
  inviteCandidate,
  listRelationshipsForUser,
  migrateLegacyRelationships,
  requestCoach
} from "../services/coachRelationshipService";

function clean(value) {
  return String(value || "").trim();
}

function getPersonName(person = {}) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") ||
    person.displayName ||
    person.email ||
    "Unnamed User";
}

function formatDate(value) {
  if (!value) return "";
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function publicCandidateFields(profile = {}, includeCoachPrivate = false) {
  const fields = [
    { label: "Name", value: getPersonName(profile) },
    { label: "Email", value: profile.email },
    { label: "Phone", value: profile.phone },
    { label: "Location", value: [profile.city, profile.province].filter(Boolean).join(", ") },
    { label: "Job Role", value: profile.jobRole },
    { label: "Primary Occupation", value: profile.primaryOccupation },
    { label: "Secondary Occupation", value: profile.secondaryOccupation },
    { label: "Primary Goal", value: profile.primaryEmploymentGoals },
    { label: "Secondary Goal", value: profile.secondaryEmploymentGoals },
    { label: "Certifications", value: Array.isArray(profile.certifications) ? profile.certifications.join(", ") : "" },
    { label: "Licenses", value: Array.isArray(profile.licenses) ? profile.licenses.join(", ") : "" },
    { label: "Education", value: Array.isArray(profile.education) ? profile.education.join(", ") : "" },
    { label: "Resume Readiness", value: profile.resumeScore ? `${profile.resumeScore}` : "Not assessed" },
    { label: "Interview Readiness", value: profile.interviewScore ? `${profile.interviewScore}` : "Not assessed" },
    { label: "Communication Readiness", value: profile.communicationScore ? `${profile.communicationScore}` : "Not assessed" },
    { label: "Job Carve-Out Interest", value: profile.interestedInJobCarveOuts },
    { label: "Preferred Carve-Out Hours", value: profile.preferredCarveOutHours },
    { label: "Preferred Supervision", value: profile.preferredSupervisionLevel },
    { label: "Carve-Out Notes", value: profile.carveOutNotes }
  ];

  if (includeCoachPrivate) {
    fields.push({ label: "Career Coach Notes", value: profile.coachNotes });
  }

  return fields.filter(field => clean(field.value));
}

function Drawer({ balance }) {
  return createPortal(
    <GlobalNavDrawer
      hideCurrent
      links={[
        { to: "/purchase", label: `Tokens: ${balance}` },
        { to: "/jobbank", label: "Job Bank Home" },
        { to: "/jobbankjobs", label: "Jobs" },
        { to: "/jobcarveouts", label: "Job Carve-Outs" },
        { to: "/events", label: "Hiring Events" },
        { to: "/profile", label: "My Profile" },
        { to: "/voicebot", label: "iSpeakWell" },
        { to: "/resumerevisor", label: "Resumes" },
        { to: "/conversationpractice", label: "Convos" },
        { to: "/interview-practice", label: "Interviews" },
        { to: "/termsofservice", label: "Terms" },
        { to: "/privacypolicy", label: "Privacy" },
        { to: "/admin", label: "Admin" },
        { to: "/login", label: "Logout", onClick: async () => { await logout(); } }
      ]}
    />,
    document.body
  );
}

export default function CareerCoachPortal() {
  const { currentUser, loadingProfile } = useAuth();
  const { role, roleLoading, userData } = useJobBankRole(currentUser);
  const { balance, setBalance } = useContext(TokenContext);

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("coaches");
  const [coaches, setCoaches] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [messages, setMessages] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [referralNote, setReferralNote] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const admin = isAdmin(role);
  const canBootstrapDirectory = canBootstrapCoachDirectory(userData || {}, role);
  const coach = isCareerCoach(role);
  const employer = isEmployer(role);
  const candidate = isCandidate(role);
  const [selectedConversation,setSelectedConversation]=useState(null);
  const [employerModal, setEmployerModal] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [myEventRegistrations, setMyEventRegistrations] = useState([]);
  const [eventClientSearch, setEventClientSearch] = useState("");
  const [savingEventRegistrationId, setSavingEventRegistrationId] = useState(null);
  const [publicProfile, setPublicProfile] = useState(null);
  const [savingPublicProfile, setSavingPublicProfile] = useState(false);
  console.log("CareerCoachPortal rendered");
  useEffect(() => {
      console.log("CareerCoachPortal mounted");

      return () => {
          console.log("CareerCoachPortal unmounted");
      };
  }, []);
  useEffect(() => {
      console.log("loading =", loading);
  }, [loading]);
  useEffect(() => {
      console.log("loadAll()");
  }, []);
  useEffect(() => {
    let isMounted = true;
    async function fetchTokens() {
      if (!currentUser?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists() && isMounted) setBalance(userDoc.data().tokens ?? 0);
      } catch (error) {
        console.error("Error fetching token count:", error);
      }
    }
    fetchTokens();
    return () => { isMounted = false; };
  }, [currentUser, setBalance]);

  const groupedReferrals = useMemo(() => {

      const groups = {};

      referrals.forEach(referral => {

          if (!groups[referral.candidateUid]) {

              groups[referral.candidateUid] = {

                  candidateUid: referral.candidateUid,

                  candidateName: referral.candidateName,

                  referrals: []

              };

          }

          groups[referral.candidateUid].referrals.push(referral);

      });

      return Object.values(groups);

  }, [referrals]);

  const relationshipForCoach = useCallback((coachUid) => {
    return relationships.find(rel =>
      rel.coachUid === coachUid &&
      rel.candidateUid === currentUser?.uid &&
      rel.status !== "Ended" &&
      rel.status !== "Declined"
    );
  }, [relationships, currentUser]);

  const activeRelationshipForCandidate = useCallback((candidateUid) => {
    return relationships.find(rel =>
      rel.candidateUid === candidateUid &&
      rel.active === true &&
      rel.status === "Active"
    );
  }, [relationships]);

  const canSeeCandidateProfile = useCallback((candidateUid) => {
    if (admin) return true;
    if (!coach) return false;
    return relationships.some(rel =>
      rel.candidateUid === candidateUid &&
      rel.coachUid === currentUser?.uid &&
      rel.active === true &&
      rel.status === "Active"
    );
  }, [admin, coach, relationships, currentUser]);

  const loadCoaches = useCallback(async () => {
    return listDirectoryCoaches({
      uid: currentUser?.uid,
      userData: userData || {},
      role: role || ''
    });
  }, [currentUser, userData, role]);

  const loadEmployers = useCallback(async () => {
    const snapshot = await getDocs(query(collection(db, "employers"), orderBy("companyName")));
    return snapshot.docs.map(employerDoc => ({
      id: employerDoc.id,
      ...employerDoc.data()
    }));
  }, []);

  const loadRelationships = useCallback(async () => {
    if (!currentUser?.uid) return [];
    if (employer && !admin) return [];

    await migrateLegacyRelationships({
      uid: currentUser.uid,
      isAdmin: admin
    });

    return listRelationshipsForUser({
      uid: currentUser.uid,
      coach: coach && !admin,
      candidate: candidate && !admin,
      admin
    });
  }, [currentUser, coach, candidate, employer, admin]);

  const loadMessages = useCallback(async () => {
    if (!currentUser?.uid) return [];

    const snapshot = await getDocs(query(
      collection(db, "careerCoachMessages"),
      where("participantUids", "array-contains", currentUser.uid),
      orderBy("createdAt", "desc")
    ));

    return snapshot.docs.map(messageDoc => ({
      id: messageDoc.id,
      ...messageDoc.data()
    }));
  }, [currentUser]);
  const visibleMessages = useMemo(() => messages, [messages]);
  const conversations = useMemo(() => {
      const map = new Map();

      visibleMessages.forEach(msg => {

          const otherId =
              msg.senderUid === currentUser.uid
                  ? msg.recipientUid
                  : msg.senderUid;

          if (!map.has(otherId)) {

              map.set(otherId, {
                  uid: otherId,
                  name:
                      msg.senderUid === currentUser.uid
                          ? msg.recipientName
                          : msg.senderName,

                  type:
                      msg.senderUid === currentUser.uid
                          ? msg.recipientType
                          : msg.senderType,

                  lastMessage: msg,
                  messages: []
              });
          }

          map.get(otherId).messages.push(msg);

          if (
              !map.get(otherId).lastMessage.createdAt ||
              msg.createdAt?.seconds >
                  map.get(otherId).lastMessage.createdAt?.seconds
          ) {
              map.get(otherId).lastMessage = msg;
          }

      });
      map.forEach(conversation => {

          conversation.messages.sort(

              (a,b)=>

                  (a.createdAt?.seconds||0)-

                  (b.createdAt?.seconds||0)

          );

      });
      return Array.from(map.values()).sort(
          (a,b)=>
          (b.lastMessage.createdAt?.seconds||0)-
          (a.lastMessage.createdAt?.seconds||0)
      );

  },[visibleMessages,currentUser]);
  
  useEffect(() => {

      if (
          !selectedConversation &&
          conversations.length > 0
      ) {

          setSelectedConversation(conversations[0]);

      }

  },[conversations,selectedConversation]);

  const loadReferrals = useCallback(async () => {
    if (!currentUser?.uid) return [];

    let constraints = [];
    if (coach && !admin) constraints = [where("coachUid", "==", currentUser.uid)];
    else if (employer && !admin) constraints = [where("employerUid", "==", currentUser.uid)];
    else if (candidate && !admin) constraints = [where("candidateUid", "==", currentUser.uid)];

    const snapshot = await getDocs(query(
      collection(db, "careerCoachReferrals"),
      ...constraints,
      orderBy("createdAt", "desc")
    ));

    return snapshot.docs.map(refDoc => ({
      id: refDoc.id,
      ...refDoc.data()
    }));
  }, [currentUser, coach, employer, candidate, admin]);

  const loadAll = useCallback(async () => {
    console.log("loadAll start");
    if (!currentUser) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);

      if (canBootstrapDirectory) {
        await bootstrapCoachDirectoryProfiles({ isAdmin: true });
      } else if (coach && currentUser?.uid) {
        await ensureCoachDirectoryProfile(currentUser.uid, { syncFromUser: true });
      }

      const relationshipResults = await loadRelationships();
      setRelationships(relationshipResults);

      const [coachResults, employerResults, messageResults, referralResults] = await Promise.all([
        loadCoaches(),
        loadEmployers(),
        loadMessages(),
        loadReferrals()
      ]);

      const visibleCoachResults =
      coach && !admin
        ? coachResults.filter(coachProfile => coachProfile.id !== currentUser.uid)
        : coachResults;

      setCoaches(visibleCoachResults);
      setEmployers(employerResults);
      setMessages(messageResults);
      setReferrals(referralResults);

      // Load candidates after relationships are available. Use local relationship list for visibility.
      if (coach || admin) {
        const snapshot = await getDocs(query(collection(db, "users"), orderBy("lastName")));
        const visibleCandidates = snapshot.docs
          .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
          .filter(person =>
            person.shareWithCareerCoaches === true ||
            person.careerCoachVisible === true ||
            relationshipResults.some(rel =>
              rel.candidateUid === person.id &&
              (admin || rel.coachUid === currentUser.uid)
            )
          );
        setCandidates(visibleCandidates);
      } else {
        setCandidates([]);
      }

  if (selectedCoach?.id === currentUser.uid) {
    setSelectedCoach(null);
  } else if (!selectedCoach && visibleCoachResults.length > 0) {
    setSelectedCoach(visibleCoachResults[0]);
  }      
  if (!selectedEmployer && employerResults.length > 0) setSelectedEmployer(employerResults[0]);
    } catch (error) {
      console.error("Unable to load Career Coach Portal:", error);
    } finally {
      setLoading(false);
    }
    
  }, [currentUser, loadRelationships, loadCoaches, loadEmployers, loadMessages, loadReferrals, coach, admin, canBootstrapDirectory, selectedCoach, selectedEmployer]);

  const loadCoachEvents = useCallback(async () => {
    try {
      const [eventResults, myRegistrationResults] = await Promise.all([
        getHiringEvents(true),
        getMyEventRegistrations()
      ]);

      setEvents(eventResults);
      setMyEventRegistrations(myRegistrationResults);

      setSelectedEvent(prev => {
        if (!eventResults.length) return null;
        return eventResults.find(event => event.id === prev?.id) || eventResults[0];
      });
    } catch (error) {
      console.error("Unable to load coach events:", error);
    }
  }, []);

  useEffect(() => {
    if (!selectedEvent?.id) {
      setEventRegistrations([]);
      return;
    }

    let isMounted = true;

    async function loadSelectedEventRegistrations() {
      try {
        const results = await getEventRegistrations(selectedEvent.id);
        if (isMounted) setEventRegistrations(results);
      } catch (error) {
        console.error("Unable to load event registrations:", error);
      }
    }

    loadSelectedEventRegistrations();

    return () => {
      isMounted = false;
    };
  }, [selectedEvent?.id]);

  const loadPublicProfileEditor = useCallback(async () => {
    if (!currentUser?.uid || !(coach || admin)) return;

    const profile = await loadCoachPublicProfileForEdit(currentUser.uid);

    setPublicProfile(
      profile || {
        uid: currentUser.uid,
        firstName: "",
        lastName: "",
        coachBio: "",
        specialties: "",
        listedInDirectory: true,
        showEmail: false,
        showPhone: false,
      }
    );
  }, [currentUser?.uid, coach, admin]);

  useEffect(() => {
      if (loadingProfile || roleLoading) return;

      if (!currentUser) {
          redirectToJobBankLogin("/careercoaches");
          return;
      }

      if (initialized) return;

      setInitialized(true);
      loadAll();
      loadCoachEvents();
  }, [currentUser, loadingProfile, roleLoading, initialized, loadAll, loadCoachEvents]);

  useEffect(() => {
    if (tab === "myProfile" && (coach || admin) && currentUser?.uid) {
      loadPublicProfileEditor();
    }
  }, [tab, coach, admin, currentUser, loadPublicProfileEditor]);

  const activeEvents = useMemo(() => {
    return events
      .filter(event => event.active !== false)
      .sort((a, b) => {
        const aTime = a.startDateTime?.seconds || new Date(a.startDateTime || 0).getTime() / 1000;
        const bTime = b.startDateTime?.seconds || new Date(b.startDateTime || 0).getTime() / 1000;
        return aTime - bTime;
      });
  }, [events]);

  const selectedEventClients = useMemo(() => {
    return eventRegistrations.filter(reg => reg.attendeeType === "Candidate");
  }, [eventRegistrations]);

  const selectedEventEmployers = useMemo(() => {
    return eventRegistrations.filter(reg => reg.attendeeType === "Employer");
  }, [eventRegistrations]);

  const selectedEventCheckedIn = useMemo(() => {
    return eventRegistrations.filter(reg => reg.checkedIn === true);
  }, [eventRegistrations]);

  const createdByMeEvents = useMemo(() => {
    return events.filter(event =>
      event.createdByUid === currentUser?.uid ||
      event.createdBy === currentUser?.uid ||
      event.coachUid === currentUser?.uid
    );
  }, [events, currentUser]);

  const myClients = useMemo(() => {
    if (!(coach || admin)) return [];
    return candidates.filter(person => {
      const activeRel = activeRelationshipForCandidate(person.id);
      return activeRel && (admin || activeRel.coachUid === currentUser?.uid);
    });
  }, [candidates, coach, admin, currentUser, activeRelationshipForCandidate]);

  const clientSearchResults = useMemo(() => {
    const search = clean(eventClientSearch).toLowerCase();

    if (!search) return myClients.slice(0, 6);

    return myClients.filter(client =>
      getPersonName(client).toLowerCase().includes(search) ||
      String(client.email || "").toLowerCase().includes(search) ||
      String(client.primaryOccupation || "").toLowerCase().includes(search)
    ).slice(0, 8);
  }, [eventClientSearch, myClients]);

  const myActiveRelationship = useMemo(() => {
    if (!candidate || !currentUser?.uid) return null;
    return relationships.find(rel =>
      rel.candidateUid === currentUser.uid &&
      rel.active === true &&
      rel.status === "Active"
    );
  }, [candidate, currentUser, relationships]);

  const unclaimedCandidates = useMemo(() => {
    if (!(coach || admin)) return [];
    return candidates.filter(person => !activeRelationshipForCandidate(person.id));
  }, [candidates, coach, admin, activeRelationshipForCandidate]);

  const pendingRelationships = useMemo(() => {
    return relationships.filter(rel => rel.status === "Pending");
  }, [relationships]);

  async function createOrSignalRelationship(coachProfile) {
    if (!currentUser) return redirectToJobBankLogin("/careercoaches");

    if (!candidate && !admin) {
      alert("Only clients can request a career coach relationship from this screen.");
      return;
    }

    if (myActiveRelationship && myActiveRelationship.coachUid !== coachProfile.id) {
      alert("You already have an active career coach. End that relationship before selecting another coach.");
      return;
    }

    try {
      setSavingId(coachProfile.id);

      await requestCoach({
        candidateUid: currentUser.uid,
        candidateName: getPersonName(userData || {}),
        candidateEmail: userData?.email || currentUser.email || "",
        coachUid: coachProfile.id,
        coachName: getCoachDisplayName(coachProfile),
        coachEmail: coachProfile.email || ""
      });

      await loadAll();
    } catch (error) {
      console.error("Unable to request career coach:", error);
      alert("Unable to request this career coach.");
    } finally {
      setSavingId(null);
    }
  }

  async function inviteOrAcceptCandidate(person) {
    if (!(coach || admin) || !currentUser) return;

    try {
      setSavingId(person.id);

      const existingActive = activeRelationshipForCandidate(person.id);
      if (existingActive && existingActive.coachUid !== currentUser.uid) {
        alert("This client already has an active career coach.");
        return;
      }

      await inviteCandidate({
        candidateUid: person.id,
        candidateName: getPersonName(person),
        candidateEmail: person.email || "",
        coachUid: currentUser.uid,
        coachName: getPersonName(userData || {}),
        coachEmail: userData?.email || currentUser.email || ""
      });

      await loadAll();
    } catch (error) {
      console.error("Unable to invite or accept candidate:", error);
      alert("Unable to update the coaching relationship.");
    } finally {
      setSavingId(null);
    }
  }

  async function declineRelationshipHandler(rel) {
    try {
      setSavingId(rel.id);
      await declineRelationship({
        candidateUid: rel.candidateUid,
        coachUid: rel.coachUid
      });
      await loadAll();
    } finally {
      setSavingId(null);
    }
  }

  /*async function endRelationshipHandler(rel) {
    if (!window.confirm("End this coaching relationship?")) return;

    try {
      setSavingId(rel.id);
      await endRelationship({
        candidateUid: rel.candidateUid,
        coachUid: rel.coachUid
      });
      await loadAll();
    } catch (error) {
      console.error("Unable to end coaching relationship:", error);
      alert(error.message || "Unable to end relationship.");
    } finally {
      setSavingId(null);
    }
  }*/

  async function savePublicProfile() {
    if (!currentUser?.uid || !publicProfile) return;

    try {
      setSavingPublicProfile(true);
      await saveCoachPublicProfile(currentUser.uid, publicProfile);
      alert("Public coach profile saved.");
      await loadAll();
      await loadPublicProfileEditor();
    } catch (error) {
      console.error("Unable to save public coach profile:", error);
      alert("Unable to save public coach profile.");
    } finally {
      setSavingPublicProfile(false);
    }
  }

  /*async function endRelationship(rel) {
    if (!window.confirm("End this coaching relationship?")) return;
    try {
      setSavingId(rel.id);
      await updateDoc(doc(db, "careerCoachRelationships", rel.id), {
        status: "Ended",
        active: false,
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await loadAll();
    } finally {
      setSavingId(null);
    }
  }*/

  async function sendCoachMessage({ recipientUid, recipientName, recipientType, relationshipId = "", employerUid = "" }) {
    if (!currentUser) return redirectToJobBankLogin("/careercoaches");
    if (recipientUid === currentUser.uid) {
      alert("You cannot send a message to yourself.");
      return;
    }
    if (!clean(messageText)) return;

    // Candidates and employers may message career coaches. Career coaches/admins may message candidates/employers.
    const senderType = coach ? "Career Coach" : employer ? "Employer" : candidate ? "Candidate" : "Admin";
    const allowed = admin ||
      (candidate && recipientType === "Career Coach") ||
      (employer && recipientType === "Career Coach") ||
      (coach && ["Candidate", "Employer", "Career Coach"].includes(recipientType));

    if (!allowed) {
      alert("This message is not allowed by the Job Bank privacy rules.");
      return;
    }

    try {
      setSavingId(recipientUid);
      await addDoc(collection(db, "careerCoachMessages"), {
        participantUids: [currentUser.uid, recipientUid],
        senderUid: currentUser.uid,
        senderEmail: currentUser.email || "",
        senderName: getPersonName(userData || {}),
        senderType,
        recipientUid,
        recipientName,
        recipientType,
        relationshipId,
        employerUid,
        message: clean(messageText),
        status: "New",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setMessageText("");
      await loadAll();
    } catch (error) {
      console.error("Unable to send message:", error);
      alert("Unable to send message.");
    } finally {
      setSavingId(null);
    }
  }

  async function createReferral() {
    if (!(coach || admin) || !selectedCandidate || !selectedEmployer || !currentUser) return;
    if (!canSeeCandidateProfile(selectedCandidate.id)) {
      alert("You can only refer clients with whom you have an active coaching relationship.");
      return;
    }

    try {
      setSavingId("referral");
      await addDoc(collection(db, "careerCoachReferrals"), {
        coachUid: currentUser.uid,
        coachName: getPersonName(userData || {}),
        employerUid: selectedEmployer.id,
        employerName: selectedEmployer.companyName || "Employer",
        candidateUid: selectedCandidate.id,
        candidateName: getPersonName(selectedCandidate),
        candidateEmail: selectedCandidate.email || "",
        status: "Sent",
        note: clean(referralNote),
        includeResume: true,
        includeAssessments: true,
        includeNotes: false,
        includeMessages: false,
        profileSnapshot: {
          firstName: selectedCandidate.firstName || "",
          lastName: selectedCandidate.lastName || "",
          email: selectedCandidate.email || "",
          phone: selectedCandidate.phone || "",
          city: selectedCandidate.city || "",
          province: selectedCandidate.province || "",
          jobRole: selectedCandidate.jobRole || "",
          primaryOccupation: selectedCandidate.primaryOccupation || "",
          secondaryOccupation: selectedCandidate.secondaryOccupation || "",
          primaryEmploymentGoals: selectedCandidate.primaryEmploymentGoals || "",
          secondaryEmploymentGoals: selectedCandidate.secondaryEmploymentGoals || "",
          certifications: selectedCandidate.certifications || [],
          licenses: selectedCandidate.licenses || [],
          education: selectedCandidate.education || [],
          resumeScore: selectedCandidate.resumeScore || 0,
          interviewScore: selectedCandidate.interviewScore || 0,
          communicationScore: selectedCandidate.communicationScore || 0,
          resumeUrl: selectedCandidate.resumeUrl || selectedCandidate.resumeDownloadUrl || "",
          resumeText: selectedCandidate.resumeText || ""
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setReferralNote("");
      alert("Referral sent to employer.");
      await loadAll();
    } catch (error) {
      console.error("Unable to create referral:", error);
      alert("Unable to send referral.");
    } finally {
      setSavingId(null);
    }
  }

  if (loadingProfile || roleLoading || loading) {
    return (
      <div className="careerCoachPortal">
        <h2>Loading Career Coach Space...</h2>
        <Drawer balance={balance} />
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const tabs = [
    { key: "requests", label: "Relationship Requests", show: coach || admin || candidate },
    { key: "unclaimed", label: "Unclaimed Clients", show: coach || admin },
    { key: "messages", label: "Messages", show: true },
    { key: "clients", label: "My Clients", show: coach || admin },
    { key: "employers", label: employer ? "Message Coaches" : "Employers", show: employer || coach || admin },
    { key: "referrals", label: "Referrals", show: coach || employer || admin || candidate },
    { key: "coaches", label: "Career Coaches", show: true },
    { key: "myProfile", label: "Public Profile", show: coach || admin },
    { key: "events", label: "Events", show: coach || admin },
  ].filter(item => item.show);

  async function registerClientForSelectedEvent(person) {
    if (!selectedEvent || !person) return;

    try {
      setSavingEventRegistrationId(person.id);

      const result = await registerForHiringEvent({
        event: selectedEvent,
        attendeeType: "Candidate",
        registrationData: {
          firstName: person.firstName || "",
          lastName: person.lastName || "",
          email: person.email || "",
          phone: person.phone || "",
          candidateUid: person.id,
          coachUid: currentUser.uid,
          coachName: getPersonName(userData || {}),
          registeredBy: "Career Coach",
          notes: `Registered by ${getPersonName(userData || {})}`
        }
      });

      if (!result.success) {
        alert(result.message || result.error || "Unable to register client.");
        return;
      }

      setEventClientSearch("");

      const updatedRegistrations = await getEventRegistrations(selectedEvent.id);
      setEventRegistrations(updatedRegistrations);

      const updatedMyRegistrations = await getMyEventRegistrations();
      setMyEventRegistrations(updatedMyRegistrations);

    } catch (error) {
      console.error("Unable to register client for event:", error);
      alert("Unable to register client.");
    } finally {
      setSavingEventRegistrationId(null);
    }
  }

  async function toggleEventCheckIn(registration) {
    try {
      setSavingEventRegistrationId(registration.id);

      await updateEventRegistration(registration.id, {
        checkedIn: !registration.checkedIn,
        status: registration.checkedIn ? "Registered" : "Checked In"
      });

      if (selectedEvent?.id) {
        const updatedRegistrations = await getEventRegistrations(selectedEvent.id);
        setEventRegistrations(updatedRegistrations);
      }
    } catch (error) {
      console.error("Unable to update check-in:", error);
      alert("Unable to update check-in.");
    } finally {
      setSavingEventRegistrationId(null);
    }
  }


  return (
    <div className="careerCoachPortal">
      <div className="coachHeader">
        <div>
          <p className="coachEyebrow">The iSpeakWell Project Job Bank</p>
          <h1>Career Coach Space</h1>
          <p>
            Connect clients, career coaches, and employers while protecting client privacy.
          </p>
        </div>
        <button
          onClick={() => {
            loadAll();
            loadCoachEvents();
          }}
        >
          Refresh
        </button>
      </div>

      <div className="coachTabs">
        {tabs.map(item => (
          <button
            key={item.key}
            className={tab === item.key ? "active" : ""}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "coaches" && (
        <div className="coachPanel fullCoachPanel">
          <h2>Career Coaches</h2>
          <p>
            Clients may request one active career coach. Employers may message career coaches.
          </p>

          <div className="publicDirectoryGrid">
            {coaches.map(coachProfile => {
              const rel = relationshipForCoach(coachProfile.id);
              const active = rel?.active === true;
              const pending = rel?.status === "Pending";
              const contact = getPublicContactFields(coachProfile);

              return (
                <article className="publicProfileCard" key={coachProfile.id}>
                  <h2>{getCoachDisplayName(coachProfile)}</h2>
                  <p>Career Coach</p>
                  <p>{coachProfile.coachBio || "Career coach profile."}</p>

                  <div className="publicProfileMeta">
                    <div><strong>Email</strong><p>{contact.email || "Not listed publicly"}</p></div>
                    <div><strong>Location</strong><p>{[coachProfile.city, coachProfile.province].filter(Boolean).join(", ") || "Not set"}</p></div>
                    <div><strong>Specialties</strong><p>{coachProfile.specialties || "Not set"}</p></div>
                    {rel && <div><strong>Relationship</strong><p>{active ? "Active" : pending ? "Pending" : rel.status}</p></div>}
                  </div>

                  <div className="publicProfileActions">
                    {candidate && (
                      <button
                        onClick={() => createOrSignalRelationship(coachProfile)}
                        disabled={savingId === coachProfile.id || (myActiveRelationship && !active)}
                      >
                        {active ? "Your Career Coach" : pending ? "Signal / Confirm Interest" : "Request Career Coach"}
                      </button>
                    )}

                    {coachProfile.id === currentUser?.uid ? (
                      <button
                        className="secondary"
                        onClick={() => setTab("messages")}
                      >
                        Messages
                      </button>
                    ) : (
                      <button
                        className="secondary"
                        onClick={() => setSelectedCoach(coachProfile)}
                      >
                        Message
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {selectedCoach && (
            <section className="publicProfileMessageCard">
              <h2>Message {getPersonName(selectedCoach)}</h2>
              <textarea
                value={messageText}
                placeholder="Write your message to this career coach..."
                onChange={(event) => setMessageText(event.target.value)}
              />
              <button
                onClick={() => sendCoachMessage({
                  recipientUid: selectedCoach.id,
                  recipientName: getPersonName(selectedCoach),
                  recipientType: "Career Coach"
                })}
                disabled={!clean(messageText) || savingId === selectedCoach.id}
              >
                Send Message
              </button>
            </section>
          )}
        </div>
      )}

      {tab === "requests" && (
        <div className="coachPanel fullCoachPanel">
          <h2>Relationship Requests</h2>
          {pendingRelationships.length === 0 ? (
            <p>No pending relationship requests.</p>
          ) : pendingRelationships.map(rel => (
            <div className="coachCarveOutCard" key={rel.id}>
              <div>
                <h3>{rel.candidateName}</h3>
                <p>Career Coach: {rel.coachName}</p>
                <p>Candidate requested: {rel.candidateRequested ? "Yes" : "No"}</p>
                <p>Coach invited/accepted: {rel.coachInvited || rel.coachAccepted ? "Yes" : "No"}</p>
              </div>
              <span>{rel.status}</span>
              <small>{formatDate(rel.updatedAt || rel.createdAt)}</small>
              <div className="publicProfileActions">
                {candidate && rel.candidateUid === currentUser.uid && (
                  <button onClick={() => createOrSignalRelationship({ id: rel.coachUid, firstName: rel.coachName })}>
                    Confirm Interest
                  </button>
                )}
                {(coach || admin) && rel.coachUid === currentUser.uid && (
                  <button onClick={() => inviteOrAcceptCandidate({ id: rel.candidateUid, firstName: rel.candidateName })}>
                    Accept / Invite
                  </button>
                )}
                <button className="secondary" onClick={() => declineRelationshipHandler(rel)}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "clients" && (coach || admin) && (
        <div className="coachClientLayout">
          <div className="coachPanel clientListPanel">
            <h2>My Clients</h2>
            {myClients.length === 0 ? <p>No active clients yet.</p> : myClients.map(person => (
              <button
                className={`coachClientCard ${selectedCandidate?.id === person.id ? "selected" : ""}`}
                key={person.id}
                onClick={() => setSelectedCandidate(person)}
              >
                <strong>{getPersonName(person)}</strong>
                <span>{person.primaryOccupation || person.jobRole || "No occupation set"}</span>
                <small>{[person.city, person.province].filter(Boolean).join(", ") || "Location not set"}</small>
              </button>
            ))}
          </div>

          <div className="coachPanel clientDetailPanel">
            {!selectedCandidate ? <p>Select a client.</p> : (
              <CandidateProfileReadOnly
                candidate={selectedCandidate}
                includeCoachPrivate
                onMessage={() => sendCoachMessage({
                  recipientUid: selectedCandidate.id,
                  recipientName: getPersonName(selectedCandidate),
                  recipientType: "Candidate",
                  relationshipId: activeRelationshipForCandidate(selectedCandidate.id)?.id || ""
                })}
                messageText={messageText}
                setMessageText={setMessageText}
                employers={employers}
                selectedEmployer={selectedEmployer}
                setSelectedEmployer={setSelectedEmployer}
                referralNote={referralNote}
                setReferralNote={setReferralNote}
                createReferral={createReferral}
                savingId={savingId}
              />
            )}
          </div>
        </div>
      )}

      {tab === "unclaimed" && (coach || admin) && (
        <div className="coachPanel fullCoachPanel">
          <h2>Unclaimed Clients</h2>
          <p>These clients have chosen to share their profile with career coaches and do not currently have an active coach.</p>
          <div className="publicDirectoryGrid">
            {unclaimedCandidates.map(person => (
              <article className="publicProfileCard" key={person.id}>
                <h2>{getPersonName(person)}</h2>
                <p>{person.jobRole || person.primaryOccupation || "Client"}</p>
                <p>{person.primaryEmploymentGoals || "No goal provided."}</p>
                <div className="publicProfileMeta">
                  <div><strong>Location</strong><p>{[person.city, person.province].filter(Boolean).join(", ") || "Not set"}</p></div>
                  <div><strong>Carve-Out Interest</strong><p>{person.interestedInJobCarveOuts || "Not set"}</p></div>
                </div>
                <div className="publicProfileActions">
                  <button onClick={() => inviteOrAcceptCandidate(person)} disabled={savingId === person.id}>
                    Invite / Signal Interest
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {tab === "employers" && (employer || coach || admin) && (
        <div className="coachEmployerLayout">

            <aside className="coachEmployerList">

                {employers.map(org => (

                    <button
                        key={org.id}
                        className={`coachEmployerCard ${
                            selectedEmployer?.id === org.id
                                ? "selected"
                                : ""
                        }`}
                        onClick={() => setSelectedEmployer(org)}
                    >

                        <strong>
                            {org.companyName || "Employer"}
                        </strong>

                        <span>
                            {org.industry || "Employer"}
                        </span>

                        <small>
                            {[org.city, org.province]
                                .filter(Boolean)
                                .join(", ")}
                        </small>

                    </button>

                ))}

            </aside>

            <section className="coachEmployerPanel">

                {!selectedEmployer ? (

                    <div className="coachEmptyState">

                        Select an employer.

                    </div>

                ) : (

                    <EmployerSummary
                        employer={selectedEmployer}
                        onMessage={() => {

                        setSelectedConversation({
                            uid: selectedEmployer.id,
                            name: selectedEmployer.companyName,
                            type: "Employer",
                            messages: visibleMessages
                                .filter(
                                    m =>
                                        (m.senderUid === selectedEmployer.id &&
                                        m.recipientUid === currentUser.uid) ||
                                        (m.senderUid === currentUser.uid &&
                                        m.recipientUid === selectedEmployer.id)
                                )
                                .sort(
                                    (a, b) =>
                                        (a.createdAt?.seconds || 0) -
                                        (b.createdAt?.seconds || 0)
                                )
                        });

                        setTab("messages");

                    }}
                    />

                )}

            </section>

        </div>
    )}

      {tab === "messages" && (
        <div className="coachMessageLayout">

              <aside className="coachConversationList">

                  {conversations.map(conversation=>(

                      <button
                          key={conversation.uid}
                          className={
                              selectedConversation?.uid===conversation.uid
                              ? "coachConversationCard selected"
                              : "coachConversationCard"
                          }
                          onClick={()=>
                              setSelectedConversation(conversation)
                          }
                      >

                          <strong>{conversation.name}</strong>

                          <span>{conversation.type}</span>

                          <small>
                              {conversation.lastMessage.message.slice(0,70)}
                          </small>

                      </button>
                  ))}

              </aside>

            <section className="coachConversationPanel">

                {!selectedConversation ? (

                    <div className="coachPanel fullCoachPanel">

                        <h2>Select a conversation</h2>

                        <p>
                            Choose someone from the left to view your conversation.
                        </p>

                    </div>

                ) : (

                    <div className="coachPanel fullCoachPanel">

                        <h2>{selectedConversation.name}</h2>

                        <p>{selectedConversation.type}</p>

                        <div className="coachConversationMessages">

                            {selectedConversation.messages.map(message => {

                                const mine =
                                    message.senderUid === currentUser.uid;

                                return (

                                    <div
                                      key={message.id}
                                      className={`coachMessageBubble ${
                                      mine ? "mine" : "theirs"
                                      }`}
                                    >

                                        <div className="coachMessageHeader">

                                            <small>

                                                {formatDate(message.createdAt)}

                                            </small>

                                        </div>

                                        <p>{message.message}</p>

                                    </div>

                                );

                            })}

                        </div>
                        <div className="coachComposer">
                          <textarea

                              value={messageText}

                              placeholder="Write your reply..."

                              onChange={e=>setMessageText(e.target.value)}

                          />
                        
                          <button

                              onClick={()=>sendCoachMessage({

                                  recipientUid:selectedConversation.uid,

                                  recipientName:selectedConversation.name,

                                  recipientType:selectedConversation.type

                              })}

                              disabled={!clean(messageText)}

                          >

                              Send

                          </button>
                        </div>
                    </div>

                )}

            </section>

        </div>
        
      )}

      {tab === "myProfile" && (coach || admin) && publicProfile && (
        <div className="coachPanel fullCoachPanel">
          <h2>Public Coach Profile</h2>
          <p>
            This information appears in the Career Coach directory. Identity fields
            are also kept in sync with your user profile.
          </p>

          <div className="coachPublicProfileForm">
            <label>
              First Name
              <input
                value={publicProfile.firstName || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  firstName: event.target.value
                })}
              />
            </label>

            <label>
              Last Name
              <input
                value={publicProfile.lastName || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  lastName: event.target.value
                })}
              />
            </label>

            <label>
              City
              <input
                value={publicProfile.city || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  city: event.target.value
                })}
              />
            </label>

            <label>
              Province
              <input
                value={publicProfile.province || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  province: event.target.value
                })}
              />
            </label>

            <label>
              Public Bio
              <textarea
                value={publicProfile.coachBio || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  coachBio: event.target.value
                })}
              />
            </label>

            <label>
              Specialties
              <input
                value={publicProfile.specialties || ""}
                placeholder="Comma-separated specialties"
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  specialties: event.target.value
                })}
              />
            </label>

            <label>
              Email
              <input
                value={publicProfile.email || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  email: event.target.value
                })}
              />
            </label>

            <label>
              Phone
              <input
                value={publicProfile.phone || ""}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  phone: event.target.value
                })}
              />
            </label>

            <label className="coachCheckboxField">
              <input
                type="checkbox"
                checked={publicProfile.listedInDirectory !== false}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  listedInDirectory: event.target.checked
                })}
              />
              List me in the public Career Coach directory
            </label>

            <label className="coachCheckboxField">
              <input
                type="checkbox"
                checked={publicProfile.showEmail === true}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  showEmail: event.target.checked
                })}
              />
              Show email on public profile
            </label>

            <label className="coachCheckboxField">
              <input
                type="checkbox"
                checked={publicProfile.showPhone === true}
                onChange={(event) => setPublicProfile({
                  ...publicProfile,
                  showPhone: event.target.checked
                })}
              />
              Show phone on public profile
            </label>
          </div>

          <div className="publicProfileActions">
            <button
              onClick={savePublicProfile}
              disabled={savingPublicProfile}
            >
              {savingPublicProfile ? "Saving..." : "Save Public Profile"}
            </button>
          </div>
        </div>
      )}

      {tab === "events" && (coach || admin) && (
        <div className="coachEventsTab">

          <div className="coachStatsGrid">
            <div className="coachStatCard">
              <span>{activeEvents.length}</span>
              <p>Upcoming Events</p>
            </div>

            <div className="coachStatCard">
              <span>{createdByMeEvents.length}</span>
              <p>Created By Me</p>
            </div>

            <div className="coachStatCard">
              <span>{myEventRegistrations.length}</span>
              <p>My Registrations</p>
            </div>

            <div className="coachStatCard">
              <span>{selectedEventCheckedIn.length}</span>
              <p>Checked In</p>
            </div>
          </div>

          <div className="coachEventsLayout">

            <aside className="coachEventListPanel">
              <div className="coachEventListHeader">
                <h2>Events</h2>

                <button onClick={() => window.location.href = "/events"}>
                  Create Event
                </button>
              </div>

              {activeEvents.length === 0 ? (
                <p>No active events yet.</p>
              ) : (
                activeEvents.map(event => (
                  <button
                    key={event.id}
                    className={`coachEventCard ${
                      selectedEvent?.id === event.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <strong>{event.title}</strong>
                    <span>{event.eventType || "Hiring Event"}</span>
                    <small>{formatDate(event.startDateTime)}</small>
                  </button>
                ))
              )}
            </aside>

            <section className="coachEventDetailPanel">

              {!selectedEvent ? (
                <div className="coachEmptyState">
                  Select an event.
                </div>
              ) : (
                <>
                  <div className="coachEventHeader">
                    <div>
                      <h2>{selectedEvent.title}</h2>
                      <p>{selectedEvent.eventType || "Hiring Event"}</p>
                    </div>

                    <span className="coachStatusPill">
                      {selectedEvent.active !== false ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="clientInfoGrid">
                    <div>
                      <strong>Starts</strong>
                      <p>{formatDate(selectedEvent.startDateTime)}</p>
                    </div>

                    <div>
                      <strong>Ends</strong>
                      <p>{formatDate(selectedEvent.endDateTime)}</p>
                    </div>

                    <div>
                      <strong>Location</strong>
                      <p>{selectedEvent.location || "Not set"}</p>
                    </div>

                    <div>
                      <strong>Capacity</strong>
                      <p>
                        {selectedEvent.employerCapacity || 0} employers /{" "}
                        {selectedEvent.candidateCapacity || 0} clients
                      </p>
                    </div>
                  </div>

                  <section className="coachEventDescription">
                    <h3>Description</h3>
                    <p>{selectedEvent.description || "No description provided."}</p>
                  </section>

                  <div className="coachEventMiniStats">
                    <div>
                      <strong>{eventRegistrations.length}</strong>
                      <span>Total Registered</span>
                    </div>

                    <div>
                      <strong>{selectedEventClients.length}</strong>
                      <span>Clients</span>
                    </div>

                    <div>
                      <strong>{selectedEventEmployers.length}</strong>
                      <span>Employers</span>
                    </div>

                    <div>
                      <strong>{selectedEventCheckedIn.length}</strong>
                      <span>Checked In</span>
                    </div>
                  </div>

                  <section className="coachRegisterClientBox">
                    <div className="coachEventSectionHeader">
                      <h3>Register a Client</h3>
                    </div>

                    <input
                      value={eventClientSearch}
                      placeholder="Search my clients..."
                      onChange={event => setEventClientSearch(event.target.value)}
                    />

                    <div className="coachEventClientResults">
                      {clientSearchResults.length === 0 ? (
                        <p>No matching clients.</p>
                      ) : (
                        clientSearchResults.map(client => (
                          <button
                            key={client.id}
                            onClick={() => registerClientForSelectedEvent(client)}
                            disabled={savingEventRegistrationId === client.id}
                          >
                            <strong>{getPersonName(client)}</strong>
                            <span>{client.email || "No email"}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </section>

                  <div className="coachEventRegistrationGrid">
                    <section>
                      <h3>Registered Clients</h3>

                      {selectedEventClients.length === 0 ? (
                        <p>No clients registered.</p>
                      ) : (
                        selectedEventClients.map(registration => (
                          <div
                            className="coachEventRegistrationCard"
                            key={registration.id}
                          >
                            <div>
                              <strong>
                                {`${registration.firstName || ""} ${registration.lastName || ""}`.trim() || "Unnamed Client"}
                              </strong>
                              <span>{registration.email || "No email"}</span>
                              <small>{registration.status || "Registered"}</small>
                            </div>

                            <button
                              onClick={() => toggleEventCheckIn(registration)}
                              disabled={savingEventRegistrationId === registration.id}
                            >
                              {registration.checkedIn ? "Undo Check-in" : "Check In"}
                            </button>
                          </div>
                        ))
                      )}
                    </section>

                    <section>
                      <h3>Registered Employers</h3>

                      {selectedEventEmployers.length === 0 ? (
                        <p>No employers registered.</p>
                      ) : (
                        selectedEventEmployers.map(registration => (
                          <div
                            className="coachEventRegistrationCard"
                            key={registration.id}
                          >
                            <div>
                              <strong>
                                {registration.organizationName ||
                                  `${registration.firstName || ""} ${registration.lastName || ""}`.trim() ||
                                  "Unnamed Employer"}
                              </strong>
                              <span>{registration.email || "No email"}</span>
                              <small>{registration.status || "Registered"}</small>
                            </div>

                            <button
                              onClick={() => toggleEventCheckIn(registration)}
                              disabled={savingEventRegistrationId === registration.id}
                            >
                              {registration.checkedIn ? "Undo Check-in" : "Check In"}
                            </button>
                          </div>
                        ))
                      )}
                    </section>
                  </div>

                  <div className="coachEventActions">
                    <button onClick={() => window.location.href = "/events"}>
                      Open Full Events Portal
                    </button>

                    <button
                      className="secondary"
                      onClick={() => window.location.href = "/events"}
                    >
                      Edit Event
                    </button>
                  </div>
                </>
              )}

            </section>
          </div>
        </div>
      )}

      {tab === "referrals" && (
        <div className="coachPanel fullCoachPanel">
          <h2>Referrals</h2>
          {groupedReferrals.map(group => (
            <div
                key={group.candidateUid}
                className="referralGroup"
            >

                <div className="referralGroupHeader">

                    <h2>{group.candidateName}</h2>

                    <span>

                        {group.referrals.length}

                        {group.referrals.length === 1
                            ? " referral"
                            : " referrals"}

                    </span>

                </div>

                <div className="coachStatsGrid">

                    <div className="coachStatCard">

                        <span>{referrals.length}</span>

                        <p>Total Referrals</p>

                    </div>

                    <div className="coachStatCard">

                        <span>{groupedReferrals.length}</span>

                        <p>Clients Referred</p>

                    </div>

                    <div className="coachStatCard">

                        <span>

                            {new Set(referrals.map(r=>r.employerUid)).size}

                        </span>

                        <p>Employers</p>

                    </div>

                    <div className="coachStatCard">

                        <span>

                            {

                                referrals.filter(r=>r.status==="Hired").length

                            }

                        </span>

                        <p>Successful Hires</p>

                    </div>

                </div>

                {group.referrals.map(referral => (

                    <ReferralCard

                        key={referral.id}

                        referral={referral}

                        onViewClient={() => {

                            const candidate =
                                myClients.find(c =>
                                    c.id === referral.candidateUid
                                );

                            if (candidate) {

                                setSelectedCandidate(candidate);

                                setTab("clients");

                            }

                        }}

                        onViewEmployer={() => {

                            const employer =
                                employers.find(e =>
                                    e.id === referral.employerUid
                                );

                            if (employer) {

                                setSelectedEmployer(employer);

                                setTab("employers");

                            }

                        }}

                        onMessageEmployer={() => {

                            const employer =
                                employers.find(e =>
                                    e.id === referral.employerUid
                                );

                            if (!employer) return;

                            setSelectedConversation({

                                uid: employer.id,

                                name: employer.companyName,

                                type: "Employer",

                                messages:
                                    visibleMessages
                                        .filter(m =>

                                            (m.senderUid === employer.id &&
                                            m.recipientUid === currentUser.uid)

                                            ||

                                            (m.senderUid === currentUser.uid &&
                                            m.recipientUid === employer.id)

                                        )

                                        .sort((a,b)=>

                                            (a.createdAt?.seconds||0)-

                                            (b.createdAt?.seconds||0)

                                        )

                            });

                            setTab("messages");

                        }}

                    />

                ))}

            </div>

        ))}
        </div>
      )}
      {employerModal && (

          <EmployerModal
              employer={employerModal}
              close={() => setEmployerModal(null)}
          />

      )}
      <Drawer balance={balance} />
    </div>
  );
}

function CandidateProfileReadOnly({
  candidate,
  includeCoachPrivate,
  onMessage,
  messageText,
  setMessageText,
  employers,
  selectedEmployer,
  setSelectedEmployer,
  referralNote,
  setReferralNote,
  createReferral,
  savingId
}) {
  const fields = publicCandidateFields(candidate, includeCoachPrivate);

  return (
    <>
      <div className="clientDetailHeader">
        <div>
          <h2>{getPersonName(candidate)}</h2>
          <p>{candidate.email || "No email"}</p>
        </div>
        <div className="clientCompletionBadge">
          {candidate.profileCompletion || 0}% Complete
        </div>
      </div>

      <div className="clientInfoGrid">
        {fields.map(field => (
          <div key={field.label}>
            <strong>{field.label}</strong>
            <p>{field.value}</p>
          </div>
        ))}
      </div>

      <section className="coachPanel">

        <h3>Resume</h3>

        {candidate.masterResume?.resumeText ? (

            <div className="coachResumeViewer">

                <pre>
                    {candidate.masterResume.resumeText}
                </pre>

            </div>

        ) : (

            <p>No resume available.</p>

        )}

    </section>

      <section className="publicProfileMessageCard">
        <h3>Message Client</h3>
        <textarea
          value={messageText}
          placeholder="Write a message to this client..."
          onChange={(event) => setMessageText(event.target.value)}
        />
        <button onClick={onMessage} disabled={!clean(messageText)}>
          Send Message
        </button>
      </section>

      <section className="publicProfileMessageCard">
        <h3>Refer Client to Employer</h3>
        <select
          value={selectedEmployer?.id || ""}
          onChange={(event) => {
            const found = employers.find(employer => employer.id === event.target.value);
            setSelectedEmployer(found || null);
          }}
        >
          <option value="">Select employer</option>
          {employers.map(employer => (
            <option key={employer.id} value={employer.id}>
              {employer.companyName || employer.email || employer.id}
            </option>
          ))}
        </select>
        <textarea
          value={referralNote}
          placeholder="Referral note to employer. Coach notes and messages are never included."
          onChange={(event) => setReferralNote(event.target.value)}
        />
        <button
          onClick={createReferral}
          disabled={!selectedEmployer || savingId === "referral"}
        >
          Send Referral
        </button>
      </section>
    </>
  );
}

function EmployerSummary({
    employer,
    onMessage,
    openModal
}) {

    return (

        <>

            <div className="employerSummaryHeader">

                <div>

                    <h2>{employer.companyName}</h2>

                    <p>
                        {employer.industry}
                    </p>

                </div>

            </div>

            <div className="clientInfoGrid">

                <div>

                    <strong>Location</strong>

                    <p>
                        {[employer.city, employer.province]
                            .filter(Boolean)
                            .join(", ")}
                    </p>

                </div>

                <div>

                    <strong>Website</strong>

                    <p>{employer.website}</p>

                </div>

                <div>

                    <strong>Contact</strong>

                    <p>{employer.contactName}</p>

                </div>

                <div>

                    <strong>Email</strong>

                    <p>{employer.email}</p>

                </div>

                <div>

                    <strong>Hiring Region</strong>

                    <p>{employer.hiringRegions}</p>

                </div>

                <div>

                    <strong>Remote</strong>

                    <p>
                        {employer.remoteAvailable
                            ? "Yes"
                            : "No"}
                    </p>

                </div>

            </div>

            <section className="coachPanel">

                <h3>Company Description</h3>

                <p className="summaryDescription">

                    {employer.companyDescription}

                </p>

            </section>

            <div className="publicProfileActions">

                <button onClick={onMessage}>
                    Message Employer
                </button>

            </div>

        </>

    );
    
}

function ReferralCard({
    referral,
    onViewClient,
    onViewEmployer,
    onMessageEmployer
}) {

    return (

        <div className="referralCard">

            <div className="referralHeader">

                <div>

                    <h3>{referral.candidateName}</h3>

                    <p>{referral.employerName}</p>

                </div>

                <span className={`referralStatus ${referral.status.toLowerCase()}`}>
                    {referral.status}
                </span>

            </div>

            <div className="referralMeta">

                <div>

                    <strong>Coach</strong>

                    <span>{referral.coachName}</span>

                </div>

                <div>

                    <strong>Sent</strong>

                    <span>{formatDate(referral.createdAt)}</span>

                </div>

            </div>

            {clean(referral.note) && (

                <div className="referralNote">

                    <strong>Referral Note</strong>

                    <p>{referral.note}</p>

                </div>

            )}

            <div className="referralButtons">

                <button onClick={onViewClient}>
                    View Client
                </button>

                <button
                    className="secondary"
                    onClick={onViewEmployer}
                >
                    View Employer
                </button>

                <button
                    className="secondary"
                    onClick={onMessageEmployer}
                >
                    Message Employer
                </button>

            </div>

        </div>

    );

}

function EmployerModal({ employer, close }) {

    return createPortal(

        <div
            className="coachModalOverlay"
            onClick={close}
        >

            <div
                className="coachModal"
                onClick={e => e.stopPropagation()}
            >

                <button
                    className="coachModalClose"
                    onClick={close}
                >

                    ✕

                </button>

                <EmployerProfileReadOnly
                    employer={employer}
                />

            </div>

        </div>,

        document.body

    );

}
